import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import { DetailDAPSNResponseOut, EntityTypeEnum, FeaturesEnum, PermissionTypesEnum, SupplierNotificationTypeEnum, TypeEnum } from '@api/ringil-api.v2.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { formatLocationNotino, formatPieces, formatWeight, isBlank, isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import CreatedBadge from '@components/ringil3/Badges/CreatedBadge.tsx';
import UpdatedBadge from '@components/ringil3/Badges/UpdatedBadge.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import SenderBadge from '@components/ringil3/Badges/SenderBadge.tsx';
import RecieverBadge from '@components/ringil3/Badges/RecieverBadge.tsx';
import TransportationTypeTag from '@components/framework/tags/TransportationTypeTag.tsx';
import ShipmentNumberTag from '@app/pages/dap/tags/ShipmentNumberTag.tsx';
import CarrierNameTag from '@app/pages/dap/tags/CarrierNameTag.tsx';
import SNTypeBadge from '@app/pages/dap/badges/SNTypeBadge.tsx';
import { RingilDapTabs, useCreateDAPTabs } from '@app/pages/dap/tags/useCreateDapTabs.tsx';
import { useCreateAttachmentComponent } from '@components/ringil3/Features/Attachments/AttachementComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgReservation from '@components/ringil3/icons/heading/Reservation.tsx';
import { Card } from '@components/ringil3/styles.tsx';
import { ColWithGap, ColWithSmallerGap, Label } from '@components/styles.tsx';
import EditableHeading3 from '@components/ringil3/Heading/EditableHeading3.tsx';
import SvgMappoint from '@components/ringil3/icons/Mappoint.tsx';
import SvgCalendar from '@components/ringil3/icons/Calendar.tsx';
import SvgReservationManipulation from '@components/ringil3/icons/ReservationManipulation.tsx';
import RingilOrder from '@components/ringil3/RingilOrder/RingilOrder.tsx';
import { printDeliverySlots } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import { useCreateEditDAPOrdersSidebar } from '@app/pages/dap/sidebar/EditDAPOrdersSidebar.tsx';
import SvgIconNote from '@app/icons/heading/IconNote.tsx';
import { useCreateEditDAPNoteSidebar } from '@app/pages/dap/sidebar/EditDAPNoteSidebar.tsx';
import { useCreateEditDAPSupplierSidebar } from '@app/pages/dap/sidebar/EditDAPSupplierSidebar.tsx';
import { useCreateEditDAPCustomerSidebar } from '@app/pages/dap/sidebar/EditDAPCustomerSidebar.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import SvgIconPlus from '@components/framework/orders/IconPlus.tsx';
import ResponsiblePersonTag from '@components/framework/tags/ResponsiblePersonTag.tsx';
import InternalInfoNotifyTag from '@components/framework/tags/InternalInfoNotifyTag.tsx';
import InternalInfoNotifyByRoleTag from '@components/framework/tags/InternalInfoNotifyByRoleTag.tsx';
import { RoleEnum } from '@type/enums.ts';
import InternalInfoGoodsInvoiceTag from '@components/framework/tags/InternalInfoGoodsInvoiceTag.tsx';
import InternalNoteTag from '@components/framework/tags/InternalNoteTag.tsx';
import Badge from '@components/framework/badge/Badge.tsx';
import CustomsDetail from '@app/pages/Customs/CustomsDetail.tsx';
import SupplierNotificationMoreButtons from '@app/pages/customer/supplier-notification/SupplierNotificationMoreButtons.tsx';
import InternalInfoFoldersTag from '@components/framework/tags/InternalInfoFoldersTag.tsx';
import companyId from '@pages/-[lang]/admin/company/[companyId].tsx';
import SafeHtml from '@components/RingilComponents/RichTextEditor/SafeHtml.tsx';

interface ComponentProps {
  supplierNotificationId: string;
  /**
   * Shown inside another page (the SN list's split view, RD-2957): no breadcrumbs, and the heading
   * drops to h2 with the number linking to the full page. Everything else is the full detail.
   */
  embedded?: boolean;
  /** Called after the detail reloaded itself following a change — e.g. to refresh the list beside it. */
  onChanged?: () => void;
}

const SupplierNotificationDetailDap: React.FC<ComponentProps> = ({ supplierNotificationId, embedded = false, onChanged }) => {
  const {
    detailDAPSupplierNotification,
    updateTransportTypeDAPSupplierNotification,
    updateShipmentNumberDAPSupplierNotification,
    updateCarrierNameDAPSupplierNotification,
    updateResponsiblePersonDAPSupplierNotification,
    updateNotifyUsersDAPSupplierNotification,
    updateNotifyByRoleDAPSupplierNotification,
    updateInternalInvoiceDAPSupplierNotification,
    updateInternalNoteDAPSupplierNotification,
    updateFolderSupplierNotification,
  } = useContext(ApiContext);
  const [data, setData] = useState<DetailDAPSNResponseOut>();
  const nav = useNavigation();
  const { loggedUser, hasPermission } = useContext(AuthenticatedUserContext);
  const abortControllerRef = useRef<AbortController | null>(null);

  // RD-2738: per-tab DXX SN permissions (READ = see, RW = edit) replace the former ROLE_SN_RW role check.
  const canSeeDetail = hasPermission([PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_READ, PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_RW]);
  const canEditSn = hasPermission([PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_RW]);
  const canSeeInternalInfo = hasPermission([
    PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_INTERNALINFO_READ,
    PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_INTERNALINFO_RW,
  ]);
  const canEditInternalInfo = hasPermission([PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_INTERNALINFO_RW]);
  const canSeeAttachments = hasPermission([
    PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_ATTACHMENTS_READ,
    PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_ATTACHMENTS_RW,
  ]);
  const canEditAttachments = hasPermission([PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_ATTACHMENTS_RW]);
  const canSeeCustoms = hasPermission([PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_CUSTOMS_READ, PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_CUSTOMS_RW]);
  const canEditCustoms = hasPermission([PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_CUSTOMS_RW]);

  const initialPanel: RingilDapTabs = canSeeDetail ? 'detail' : canSeeAttachments ? 'attachments' : canSeeInternalInfo ? 'internalinfo' : 'customs';
  const { DapTables, selectedPanel } = useCreateDAPTabs(initialPanel);
  const { internals: attachmentInternals, load: loadAttachments, AttachmentComponent } = useCreateAttachmentComponent(
    {
      enableCopyDocument: false,
      disabled: !canEditAttachments,
      invoicesRwPermissions: [PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_ATTACHMENTS_INVOICES_RW],
      enableDownloadZip: true,
      downloadZipApplicationId: data?.applicationId,
      // Attachment changes don't go through load(), so the page beside an embedded detail is told here.
      onChanged,
    },
    {
      entityType: EntityTypeEnum.SUPPLIER_NOTIFICATION,
      entityId: supplierNotificationId,
      disabled: !canEditAttachments,
    }
  );
  const { visible, setVisible, EditDAPOrdersSidebar } = useCreateEditDAPOrdersSidebar();
  const { setVisible: setEditNoteVisible, EditDAPNoteSidebar, visible: visibleEditNote } = useCreateEditDAPNoteSidebar();
  const { setVisible: setVisibleSupplier, EditDAPSupplierSidebar, visible: visibleSupplier } = useCreateEditDAPSupplierSidebar();
  const { setVisible: setVisibleCustomer, EditDAPCustomerSidebar, visible: visibleCustomer } = useCreateEditDAPCustomerSidebar();

  // The first load of a notification is not a change; every later one follows an edit (all edit paths
  // end in load()), which is when `onChanged` tells the page around an embedded detail.
  const loadedOnceRef = useRef<string | null>(null);
  const load = () => {
    detailDAPSupplierNotification(supplierNotificationId, data => {
      if (abortControllerRef.current.signal.aborted) return;
      setData(data);
      if (loadedOnceRef.current === supplierNotificationId) onChanged?.();
      loadedOnceRef.current = supplierNotificationId;
    });
    loadAttachments(EntityTypeEnum.SUPPLIER_NOTIFICATION, supplierNotificationId);
  };

  useEffect(() => {
    const ac = new AbortController();
    abortControllerRef.current = ac;
    load();
    return () => {
      abortControllerRef.current.abort();
    };
  }, [supplierNotificationId]);

  const { tr } = useTranslationLgs();

  if (isNullOrUndefined(data) || isNullOrUndefined(loggedUser)) return <></>;
  const isOwner = loggedUser.companyId === data?.customerCompany.id;

  const isEditable = canEditSn && isOwner;
  const isInternalInfoEditable = canEditInternalInfo && isOwner;

  return (
    <div className={'flex flex-col gap-2'}>
      {!embedded && (
      <BreadCrumbComponent
        items={[
          {
            label: tr('SupplierNotificationDetailDap.list', 'Avizace'),
            url: nav.createNavigationLink(nav.urlFunctions.createSupplierNotificationList()),
          },
          { label: data.applicationId, url: null },
        ]}
      />
      )}

      <Heading
        title={data.applicationId}
        // Embedded, the number is the way to the full page.
        titleLink={embedded ? nav.createNavigationLink(nav.urlFunctions.createSupplierNotificationDetail(data.id, SupplierNotificationTypeEnum.DXX)) : undefined}
        headingVariant={embedded ? 'h2' : 'h1'}
        labels={
          <>
            {isOwner && (
              <LabelsTag
                objectId={data.id}
                objectType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
                resolveLabelsForType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
                disabled={!canEditSn}
              />
            )}
          </>
        }
        buttons={
          isEditable
            ? [
                <SupplierNotificationMoreButtons
                  key="more-buttons"
                  supplierNotificationId={data.id}
                  supplierNotificationApplicationId={data.applicationId}
                  supplierLocationName={data.supplierCompany.name}
                  onChange={() => load()}
                  archived={data.isArchived}
                  isEntityOwner={data.customerCompany?.id === loggedUser.companyId}
                  isDAP={true}
                  onCanceledSupplierNotification={() => {
                    load();
                  }}
                />,
              ]
            : []
        }
      />

      <div className={'flex flex-row gap-2'}>
        <SNTypeBadge snType={SupplierNotificationTypeEnum.DXX} />
        <SenderBadge companyName={data.supplierCompany.name} />
        <RecieverBadge companyName={data.customerCompany.name} />
        <Badge value={formatWeight(data.totalWeight)} />
        <Badge
          value={formatPieces(
            data.orders
              ?.map(t => t.items)
              .flat(1)
              .map(t => t.amount)
              .filter(t => t != null)
              .reduce((previousValue, currentValue) => {
                return previousValue + currentValue;
              }, 0),
            tr,
          )}
        />

        <CreatedBadge createdAt={data.createdAt} createdBy={data.createdBy?.fullName} timezoneId={loggedUser.defaultTimezoneId} dataTestId="sn-createdAt"/>
        <UpdatedBadge updatedAt={data.updatedAt} updatedBy={data.updatedBy?.fullName} timezoneId={loggedUser.defaultTimezoneId} dataTestId="sn-updatedAt"/>
      </div>
      <div className={'flex flex-row gap-2'}>
        <TransportationTypeTag
          transportationType={data.transportationType}
          onChange={v => {
            updateTransportTypeDAPSupplierNotification(supplierNotificationId, { transportationType: v }, () => {
              load();
            });
          }}
          readOnly={!isEditable}
        />

        <ShipmentNumberTag
          value={data.shipmentNumber}
          onChange={v => {
            updateShipmentNumberDAPSupplierNotification(supplierNotificationId, { shipmentNumber: v }, () => {
              load();
            });
          }}
          readonly={!isEditable}
        />

        <CarrierNameTag
          value={data.carrierName}
          onChange={v => {
            updateCarrierNameDAPSupplierNotification(supplierNotificationId, { carrierName: v }, () => {
              load();
            });
          }}
          readonly={!isEditable}
        />
      </div>
      <DapTables
        showDetail={canSeeDetail}
        showInternalInfo={isOwner && canSeeInternalInfo}
        showCustoms={isOwner && canSeeCustoms && loggedUser?.features?.some(t => t == FeaturesEnum.CUSTOMS)}
        showAttachments={canSeeAttachments}
      />

      {selectedPanel === 'detail' && canSeeDetail && (
        <div className={'flex flex-col gap-4 p-4'}>
          <COHeadline variant={'h2'} title={tr('SupplierNotificationDetailDap.oAvizaci', 'O avizaci')} icon={<SvgReservation />}></COHeadline>
          <div className={'grid grid-cols-2 gap-4'}>
            <div className={'flex flex-col gap-4'}>
              <Card>
                <ColWithGap>
                  <EditableHeading3
                    editable={isEditable}
                    title={tr('SupplierNotificationDetailDap.adresaNakladky', 'Adresa nakládky')}
                    icon={<SvgMappoint />}
                    onEditClick={() => {
                      setVisibleSupplier(true);
                    }}
                  ></EditableHeading3>
                  <ColWithSmallerGap>
                    <Label>{data.supplierCompany.name}</Label>
                    {formatLocationNotino(data.supplierLocation.zipCode, data.supplierLocation.city, data.supplierLocation.country, data.supplierLocation.streetNr)}
                  </ColWithSmallerGap>
                  <EditableHeading3
                    editable={false}
                    title={tr('SupplierNotificationDetailDap.datumVyzvednuti', 'Datum vyzvednutí')}
                    icon={<SvgCalendar />}
                  ></EditableHeading3>
                  <ColWithSmallerGap>{printDeliverySlots(data.supplierDeliverySlots, tr, 'supplierNotificationDetailDap-departure-timeslot')}</ColWithSmallerGap>

                  {isBlank(data.note) ? (
                    <LinkWithIcon
                      label={tr('SupplierNotificationDetailDap.poznamkaKOdesilateli', 'Poznámka k odesílateli')}
                      icon={<SvgIconPlus />}
                      onClick={() => setEditNoteVisible(true)}
                    />
                  ) : (
                    <>
                      <EditableHeading3
                        editable={isEditable}
                        title={tr('SupplierNotificationDetailDap.poznamkaKOdesilateli', 'Poznámka k odesílateli')}
                        icon={<SvgIconNote />}
                        onEditClick={() => {
                          setEditNoteVisible(true);
                        }}
                      ></EditableHeading3>
                      <SafeHtml html={data.note} />
                    </>
                  )}
                </ColWithGap>
              </Card>
              <Card>
                <ColWithGap>
                  <EditableHeading3
                    editable={isEditable}
                    title={tr('SupplierNotificationDetailDap.adresaVykladky', 'Adresa vykládky')}
                    icon={<SvgMappoint />}
                    onEditClick={() => {
                      setVisibleCustomer(true);
                    }}
                  ></EditableHeading3>
                  <ColWithSmallerGap>
                    <Label>{data.customerCompany.name}</Label>
                    {formatLocationNotino(data.customerLocation.zipCode, data.customerLocation.city, data.customerLocation.country, data.customerLocation.streetNr)}
                  </ColWithSmallerGap>
                  <EditableHeading3
                    editable={false}
                    title={tr('SupplierNotificationDetailDap.datumDodani', 'Datum dodání')}
                    icon={<SvgCalendar />}
                  />
                  <ColWithSmallerGap>{printDeliverySlots(data.customerDeliverySlots, tr, 'supplierNotificationDetailDap-arrival-timeslot')}</ColWithSmallerGap>
                </ColWithGap>
              </Card>
            </div>
            <Card>
              <EditableHeading3
                editable={isEditable}
                title={tr(`ReservationDetail.manipulation`, `Manipulation`)}
                icon={<SvgReservationManipulation />}
                onEditClick={() => {
                  setVisible(true);
                }}
              />
              <>
                <ColWithGap>
                  {data.orders.map((order, orderIndex) => {
                    return <RingilOrder key={orderIndex} type={'neutral'} order={order} />;
                  })}
                </ColWithGap>
              </>
            </Card>
          </div>
        </div>
      )}
      {selectedPanel === 'customs' && (
        <>{isOwner && canSeeCustoms && <CustomsDetail supplierNotificationId={data.id} applicationId={data.applicationId} readonly={!canEditCustoms} />}</>
      )}
      {selectedPanel === 'internalinfo' && isOwner && canSeeInternalInfo && (
        <div className={'flex flex-col gap-4'}>
          <div className={'flex flex-row gap-4'}>
            <ResponsiblePersonTag
              person={data.responsiblePerson}
              onChange={v => {
                updateResponsiblePersonDAPSupplierNotification(supplierNotificationId, { responsiblePersonId: v.id }, () => {
                  load();
                });
              }}
              readonly={!isInternalInfoEditable}
            />

            <InternalInfoNotifyTag
              persons={data.usersToNotify}
              onChange={v => {
                updateNotifyUsersDAPSupplierNotification(supplierNotificationId, { userIdsToNotify: v.map(t => t.id) }, () => {
                  load();
                });
              }}
              readonly={!isInternalInfoEditable}
            />

            <InternalInfoNotifyByRoleTag
              notifyRoleName={RoleEnum.ROLE_SN_NOTIFY}
              isActive={data.notifyUsersDependsOnRole}
              onChange={v => {
                updateNotifyByRoleDAPSupplierNotification(supplierNotificationId, { notifyUsersDependsOnRole: v }, () => {
                  load();
                });
              }}
              readonly={!isInternalInfoEditable}
            />
          </div>
          <div className={'flex flex-row gap-4'}>
            <InternalInfoGoodsInvoiceTag
              value={
                data.internalInvoiceNumber
                  ? {
                      goodsInvoiceNumber: data.internalInvoiceNumber,
                      goodsInvoiceDate: data.invoiceDueDate,
                    }
                  : null
              }
              onChange={v => {
                updateInternalInvoiceDAPSupplierNotification(
                  supplierNotificationId,
                  {
                    internalInvoiceNumber: v.goodsInvoiceNumber,
                    invoiceDueDate: v.goodsInvoiceDate,
                  },
                  () => {
                    load();
                  },
                );
              }}
              readonly={!isInternalInfoEditable}
            />

            <InternalInfoFoldersTag
              folders={(data.folders ?? []).map(f => ({ id: f.folderId, name: f.folderName, active: true }))}
              onChange={v => {
                updateFolderSupplierNotification(supplierNotificationId, { folderIds: v.map(f => f.id) }, () => {
                  load();
                });
              }}
              entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
              readonly={!isInternalInfoEditable}
            />

            <InternalNoteTag
              value={data.internalNote}
              onChange={v => {
                updateInternalNoteDAPSupplierNotification(supplierNotificationId, { internalNote: v }, () => {
                  load();
                });
              }}
              readonly={!isInternalInfoEditable}
              required={false}
            />
          </div>
        </div>
      )}
      {selectedPanel === 'attachments' && canSeeAttachments && (
        <>
          <AttachmentComponent internals={attachmentInternals} entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION} entityId={data.id} disabled={!canEditAttachments} />
        </>
      )}
      {visible && (
        <EditDAPOrdersSidebar
          supplierNotificationId={supplierNotificationId}
          applicationId={data.applicationId}
          companyId={data.customerCompany.id}
          onSaved={() => {
            load();
          }}
        />
      )}
      {visibleEditNote && (
        <EditDAPNoteSidebar
          applicationId={data.applicationId}
          onSaved={() => {
            load();
          }}
          supplierNotificationId={supplierNotificationId}
        />
      )}
      {visibleSupplier && (
        <EditDAPSupplierSidebar
          supplierNotificationId={supplierNotificationId}
          applicationId={data.applicationId}
          companyId={data.customerCompany.id}
          onSaved={() => {
            load();
          }}
        />
      )}

      {visibleCustomer && (
        <EditDAPCustomerSidebar
          supplierNotificationId={supplierNotificationId}
          applicationId={data.applicationId}
          companyId={data.customerCompany.id}
          onSaved={() => {
            load();
          }}
        />
      )}
    </div>
  );
};

export default SupplierNotificationDetailDap;
